import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useSWR from 'swr';
import { BadRequestError, getData, OAuthError, PaidFeatureError, postData } from '../backend';
import { WorkflowAutomation, WorkflowAutomationItem } from '../interfaces';
import useAutomationStatus from '../hooks/useAutomationStatus';
import SelectField from './SelectField';
import SalesforceItemRow from './AppSalesforceEmbedItemRow';

const PROCESSING_STAGES = ["INITIAL", "OUTPUT", "OUTPUT_PDF", "EMAIL"];

interface AppWorkflow {
  id: string;
  name: string;
}

function SalesforceRecordPanel(props: { objectName: string, recordId: string }) {
  const { objectName, recordId } = props;
  const { t } = useTranslation();

  const { data: workflows } = useSWR<Array<AppWorkflow>>(`/salesforce/app/${objectName}/workflows/`, getData);
  const { data: items, mutate: mutateItems } = useSWR<Array<WorkflowAutomationItem>>(
    `/salesforce/app/${objectName}/${recordId}/items/`,
    getData,
    {
      refreshInterval: (items) => items?.some(i => i.status === "PENDING" && PROCESSING_STAGES.includes(i.stage)) ? 3000 : 0,
    }
  );

  const [workflowId, setWorkflowId] = useState("");
  const [isStarting, setIsStarting] = useState(false);
  const [runError, setRunError] = useState<JSX.Element | null>(null);

  const [automation, setAutomation] = useState<WorkflowAutomation>();
  const automationStatus = useAutomationStatus(automation?.id);

  // Items are created as the automation runs, so refresh the list as it progresses
  useEffect(() => {
    if (automationStatus)
      mutateItems();
  }, [automationStatus?.status, automationStatus?.itemsCompleted, mutateItems]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!workflowId && workflows?.length)
      setWorkflowId(workflows[0].id);
  }, [workflows, workflowId]);

  async function handleRun() {
    setRunError(null);
    setIsStarting(true);

    try {
      const automation = await postData(`/workflows/${workflowId}/run/`, {
        options: {
          mode: "SOURCE_ITEM_IDS",
          sourceItemIds: [recordId],
          concurrent: true,
        }
      });

      setAutomation(automation);
      mutateItems();
    } catch (error) {
      if (error instanceof OAuthError)
        setRunError(
          <>
            {t("An integration needs to be reconnected.")}{" "}
            <a className="underline" href={error.oauthUrl} target="_blank" rel="noreferrer">{t("Reconnect")}</a>
          </>
        );
      else if (error instanceof PaidFeatureError)
        setRunError(<>{t("Your plan does not allow this workflow to be run, please upgrade in Portant.")}</>);
      else if (error instanceof BadRequestError && error.reason === "MISSING_FIELDS")
        setRunError(<>{t("This record is missing fields required by the workflow.")}</>);
      else
        setRunError(<>{t("Something went wrong running this workflow.")}</>);
    } finally {
      setIsStarting(false);
    }
  }

  const isRunning = isStarting || ["PENDING", "RUNNING"].includes(automationStatus?.status ?? "");

  return (
    <>
      <div className="flex flex-col gap-2">
        <div className="text-sm font-semibold text-gray-600 font-gilroy">{t("Run a workflow")}</div>
        {!workflows
          ? <img src="https://static.portant.co/portant-loading-gray.svg" className="w-8 h-8" alt="Loading" />
          : workflows.length === 0
            ? <div className="text-sm text-gray-500">{t("There are no completed workflows for this Salesforce object.")}</div>
            : (
              <div className="flex gap-2">
                <SelectField className="flex-grow min-w-0" value={workflowId} onChange={setWorkflowId}>
                  {workflows.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
                </SelectField>
                <button className="btn btn-blue" onClick={handleRun} disabled={!workflowId || isRunning}>
                  {isRunning ? t("Running...") : t("Run")}
                </button>
              </div>
            )
        }
        {runError && <div className="text-sm text-red">{runError}</div>}
      </div>

      <div className="flex flex-col gap-1">
        <div className="text-sm font-semibold text-gray-600 font-gilroy">{t("Previous runs")}</div>
        {!items
          ? <img src="https://static.portant.co/portant-loading-gray.svg" className="w-8 h-8" alt="Loading" />
          : items.length === 0
            ? <div className="text-sm text-gray-500">{t("No workflows have been run for this record yet.")}</div>
            : items.map(item => <SalesforceItemRow key={item.id} item={item} />)
        }
      </div>
    </>
  );
}

export default SalesforceRecordPanel;
